import { useState, useEffect } from 'react';
import { db } from '@/lib/supabaseQuery';
import { LocationWithCoordinates } from '@/types/taxExplorer';

/**
 * Hook to fetch locations with geographic coordinates for map rendering.
 * 
 * @description
 * Specialized hook for MAP COMPONENTS that need location coordinates
 * along with basic location data.
 * 
 * **Data Source:** `locations` table (includes coordinates field)
 * 
 * **When to use:**
 * - Google Maps / interactive map components
 * - Location visualization with geographic data
 * - Tax Explorer map views
 * 
 * **When NOT to use:**
 * - Simple location lists without maps → use `useLocations` or `useLocalizedLocations`
 * - URL slug generation → use `useTranslatedSlug()`
 * 
 * @returns {{ locations: LocationWithCoordinates[], isLoading: boolean }}
 * 
 * @example
 * const { locations, isLoading } = useLocationsWithCoordinates();
 * // Returns: [{ location_id: "1", coordinates: "40.4168,-3.7038", ... }]
 */
export const useLocationsWithCoordinates = () => {
  const [locations, setLocations] = useState<LocationWithCoordinates[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLocations = async () => {
      setIsLoading(true);
      try {
        const data = await db.fetchAll<{
          location_id: number | null;
          iso_alpha2: string | null;
          name: string | null;
          short_name: string | null;
          native_slug: string | null;
          native_name: string | null;
          slug: string | null;
          coordinates: string | null;
        }>(
          'locations',
          'location_id, iso_alpha2, name, short_name, native_slug, native_name, slug, coordinates',
          {
            filters: [
            { column: 'location_id', operator: 'neq', value: null },
              { column: 'iso_alpha2', operator: 'neq', value: null }
            ],
            orderBy: { column: 'name' }
          }
        );
        
        if (data) {
          setLocations(data.map(d => ({ ...d, location_id: d.location_id as number })) as LocationWithCoordinates[]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLocations();
  }, []);

  return { locations, isLoading };
};
